import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Phone } from 'lucide-react'
import { CalendlyBtn } from '@/components/ui/calendly-btn'
import { ButtonLink } from '@/components/ui/button-link'
import { footer, phone } from '@/content/site'

export default function Contact() {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] })
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '10%'])

  return (
    <section ref={ref} id="contact" className="relative py-24 lg:py-32 overflow-hidden bg-slate-900">
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 bg-gradient-to-br from-blue-900/50 via-slate-900 to-indigo-900/50 pointer-events-none"
      />

      {/* Ambient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      {/* Decorative quote mark */}
      <div className="absolute left-8 top-8 opacity-5 text-white pointer-events-none select-none">
        <img
          src={`${import.meta.env.BASE_URL}comma.webp`}
          alt=""
          className="w-24 h-auto brightness-0 invert"
        />
      </div>

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-4"
        >
          Get Started
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-4xl sm:text-5xl font-bold text-white mb-4"
        >
          {footer.heading}
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-slate-300 text-lg mb-10"
        >
          {footer.subheading}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <CalendlyBtn
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white border-transparent"
          >
            {footer.cta}
          </CalendlyBtn>
          <ButtonLink
            href={`tel:${phone}`}
            size="lg"
            variant="outline"
            className="bg-transparent border-white/30 text-white hover:bg-white/10 flex items-center gap-2"
          >
            <Phone size={16} />
            {phone}
          </ButtonLink>
        </motion.div>
      </div>
    </section>
  )
}
